import { useMemo } from "react";
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  PauseCircleIcon,
  ProhibitIcon,
  TrayIcon,
  WarningOctagonIcon,
} from "@phosphor-icons/react";
import {
  loadLeadTrend,
  loadToday,
  siteName,
  type TodaySnapshot,
  type TrendDay,
} from "../lib/data.ts";
import { useAsync, useDebounced } from "../lib/useAsync.ts";
import { duration, longDate, plural, relativeTime } from "../lib/format.ts";
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Panel,
  PopNumber,
  RowSkeleton,
} from "../components/ui.tsx";
import { useLiveRefresh } from "../lib/live.ts";
import type { LeadRecord } from "@pipeline/types.ts";

/** One thing a human should look at, with somewhere to go about it. */
interface AttentionItem {
  key: string;
  tone: "bad" | "warn" | "info";
  icon: React.ReactNode;
  text: string;
  note?: string;
  goto?: { label: string; href: string };
}

function buildAttention(snapshot: TodaySnapshot): AttentionItem[] {
  const { report, oldestPendingMs } = snapshot;
  const items: AttentionItem[] = [];

  if (report.deadDeliveries > 0) {
    items.push({
      key: "dead",
      tone: "bad",
      icon: <ProhibitIcon size={16} weight="bold" />,
      text: `${report.deadDeliveries} ${plural(report.deadDeliveries, "delivery", "deliveries")} gave up permanently`,
      note: "These leads never reached the partner.",
      goto: { label: "Fix", href: "#/deliveries" },
    });
  }

  if (report.heldForReview > 0) {
    items.push({
      key: "held",
      tone: "warn",
      icon: <PauseCircleIcon size={16} weight="bold" />,
      text: `${report.heldForReview} ${plural(report.heldForReview, "lead")} held for review`,
      note: "Not forwarded. Release anything genuine.",
      goto: { label: "Review", href: "#/held" },
    });
  }

  // A backlog only matters once it is old; a few seconds is the pipeline working.
  if (oldestPendingMs !== null && oldestPendingMs > 10 * 60_000) {
    items.push({
      key: "backlog",
      tone: "warn",
      icon: <ClockIcon size={16} weight="bold" />,
      text: `Oldest queued delivery has been waiting ${duration(oldestPendingMs)}`,
      note: "The worker may be down or the mail provider is failing.",
    });
  }

  const unrouted = snapshot.leads.filter((lead) => lead.routingStatus === "pending").length;
  if (unrouted > 0) {
    items.push({
      key: "unrouted",
      tone: "info",
      icon: <TrayIcon size={16} weight="bold" />,
      text: `${unrouted} ${plural(unrouted, "lead")} waiting to be routed`,
      note: "Normally clears within seconds.",
    });
  }

  if (report.totalLeads > 0 && report.spam / report.totalLeads > 0.5) {
    items.push({
      key: "spam",
      tone: "warn",
      icon: <WarningOctagonIcon size={16} weight="bold" />,
      text: `${Math.round((report.spam / report.totalLeads) * 100)}% of today's submissions were spam`,
      note: "Worth checking the forms for bot traffic.",
    });
  }

  return items;
}

function qualityTone(lead: LeadRecord) {
  if (lead.isDuplicate) return { tone: "neutral" as const, label: "duplicate" };
  if (lead.quality === "spam") return { tone: "bad" as const, label: "spam" };
  if (lead.quality === "suspect") return { tone: "warn" as const, label: "held" };
  if (lead.routingStatus === "routed") return { tone: "ok" as const, label: "forwarded" };
  return { tone: "accent" as const, label: "new" };
}

/**
 * Fourteen days of volume, as bars.
 *
 * Today is by definition a single day, which left the screen with no sense of
 * whether that number is good. This is the cheapest honest context: is today
 * normal, and is the pipeline actually forwarding what it captures.
 *
 * Deliberately not a line chart — daily counts are discrete events, and a line
 * implies a continuous quantity that was interpolated between the points.
 */
function TrendChart({ days, loading }: { days: TrendDay[]; loading: boolean }) {
  const peak = Math.max(1, ...days.map((day) => day.total));
  const total = days.reduce((sum, day) => sum + day.total, 0);

  if (loading) {
    return (
      <div className="trend" aria-hidden="true">
        <div className="trend__bars">
          {Array.from({ length: 14 }, (_, index) => (
            <div className="trend__col" key={index}>
              <div className="skel trend__skel" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Nothing at all in fourteen days. Drawing fourteen baseline ticks looks
  // like a rendering fault rather than an honest zero.
  if (total === 0) {
    return (
      <figure className="trend">
        <div className="trend--empty">
          No leads in the last 14 days.
          <br />
          New submissions appear here within seconds of arriving.
        </div>
      </figure>
    );
  }

  return (
    <figure className="trend">
      <div className="trend__bars">
        {days.map((day, index) => {
          const isToday = index === days.length - 1;
          // A zero day must still show a visible baseline tick, otherwise a
          // quiet day is indistinguishable from a gap in the data.
          const height = day.total === 0 ? 2 : Math.max(6, (day.total / peak) * 100);
          const forwardedShare = day.total === 0 ? 0 : (day.forwarded / day.total) * 100;
          return (
            <div
              className="trend__col"
              key={day.dateKey}
              data-today={isToday || undefined}
              title={`${day.dateKey}: ${day.total} ${plural(day.total, "lead")}, ${day.forwarded} forwarded`}
            >
              <div className="trend__bar" style={{ height: `${height}%` }}>
                <div className="trend__bar-fill" style={{ height: `${forwardedShare}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      <figcaption className="trend__caption">
        <span>last 14 days</span>
        <span>
          <strong data-numeric>{total}</strong> {plural(total, "lead")} ·{" "}
          <span className="trend__key" aria-hidden="true" /> forwarded
        </span>
      </figcaption>
    </figure>
  );
}

export function Today() {
  const state = useAsync<TodaySnapshot>(() => loadToday(), []);
  const trend = useAsync<TrendDay[]>(() => loadLeadTrend(14), []);
  // The trend's last bar is today, so a realtime insert has to move it too or
  // the chart quietly disagrees with the figure directly above it.
  const refresh = useDebounced(() => {
    state.reload();
    trend.reload();
  }, 800);
  const connected = useLiveRefresh(refresh);

  const attention = useMemo(
    () => (state.data ? buildAttention(state.data) : []),
    [state.data],
  );

  const recent = useMemo(
    () => (state.data ? state.data.leads.slice(0, 12) : []),
    [state.data],
  );

  /** Top producing sites today, as a share of the day's total. */
  const topSites = useMemo(() => {
    const report = state.data?.report;
  const hasMedian = !!report && report.medianMinutesToForward !== null;
    if (!report || report.totalLeads === 0) return [];
    const busiest = report.bySite[0]?.total ?? 1;
    return report.bySite.slice(0, 4).map((site) => ({
      ...site,
      share: Math.max(6, Math.round((site.total / busiest) * 100)),
    }));
  }, [state.data]);

  if (state.error && !state.data) {
    return <ErrorState message={state.error} onRetry={state.reload} />;
  }

  const report = state.data?.report;
  const hasMedian = !!report && report.medianMinutesToForward !== null;

  return (
    <div className="stack stack--5">
      <header className="page-head">
        <div>
          <h1 className="page-head__title">
            {state.data ? longDate(state.data.dateKey) : "Today"}
          </h1>
          <p className="page-head__sub">
            {state.loading
              ? "Loading today's activity"
              : `${report?.totalLeads ?? 0} ${plural(report?.totalLeads ?? 0, "submission")} so far`}
          </p>
        </div>
        <span className="live" data-state={connected ? "live" : "offline"}>
          <span className="live__dot" aria-hidden="true" />
          {connected ? "Live" : "Reconnecting"}
        </span>
      </header>

      {/* Summary and attention share a row on desktop. Stacked full-width they
          pushed the actual submissions below the fold, which inverted the
          priority: the day's leads are the thing you came to look at. */}
      <div className="today-grid">
        <section className="panel today-summary">
          <div className="today-summary__head">
            <div>
              {/* The one figure that gets number motion. Doing it to all four
                  would be a choreographed entrance wearing a state-change
                  costume; this one changes on its own while you watch. */}
              <span className="stat-hero__value" data-numeric>
                <PopNumber value={state.loading ? "--" : String(report?.totalLeads ?? 0)} />
              </span>
              <p className="stat-hero__label">
                {plural(report?.totalLeads ?? 0, "lead")} captured today
              </p>
            </div>

            {/* Inline, not tiles. Three boxed cells for three small numbers was
                most of the wasted space. */}
            <dl className="quickstats">
              <div className="quickstat">
                <dt>clean</dt>
                <dd data-tone="ok" data-numeric>
                  {state.loading ? "--" : (report?.clean ?? 0)}
                </dd>
              </div>
              <div className="quickstat">
                <dt>held</dt>
                <dd data-tone={(report?.heldForReview ?? 0) > 0 ? "warn" : "muted"} data-numeric>
                  {state.loading ? "--" : (report?.heldForReview ?? 0)}
                </dd>
              </div>
              <div className="quickstat">
                <dt>failed</dt>
                <dd data-tone={(report?.deadDeliveries ?? 0) > 0 ? "bad" : "muted"} data-numeric>
                  {state.loading ? "--" : (report?.deadDeliveries ?? 0)}
                </dd>
              </div>
            </dl>
          </div>

          <TrendChart days={trend.data ?? []} loading={trend.loading} />

          {/* Only rendered when it has something to say. An empty footer
              still drew its top border, which read as a stray rule under the
              chart on a quiet day. */}
          {(hasMedian || topSites.length > 0) && (
            <div className="today-summary__foot">
              {hasMedian ? (
                <span>
                  median{" "}
                  <strong data-numeric>
                    {report!.medianMinutesToForward! < 1
                      ? "<1"
                      : Math.round(report!.medianMinutesToForward!)}
                  </strong>{" "}
                  min to forward
                </span>
              ) : (
                <span />
              )}
              {topSites.length > 0 && (
                <span className="today-summary__sites">
                  busiest:{" "}
                  {topSites.slice(0, 2).map((site, index) => (
                    <span key={site.siteKey}>
                      {index > 0 && ", "}
                      {site.siteName} <strong data-numeric>{site.total}</strong>
                    </span>
                  ))}
                </span>
              )}
            </div>
          )}
        </section>

        <Panel title="Needs attention" flush className="today-attention">
        {state.loading ? (
          <div style={{ padding: "var(--sp-5)" }}>
            <div className="skel" style={{ height: 15, width: "45%" }} />
          </div>
        ) : attention.length === 0 ? (
          <div className="allclear">
            <CheckCircleIcon size={19} weight="fill" className="allclear__icon" aria-hidden="true" />
            <span>Nothing needs you right now. Everything forwarded cleanly.</span>
          </div>
        ) : (
          <div className="attention">
            {attention.map((item) => (
              <div className="attention__item" key={item.key}>
                <span className="attention__icon" data-tone={item.tone} aria-hidden="true">
                  {item.icon}
                </span>
                <span className="attention__text">
                  {item.text}
                  {item.note && <span className="attention__note">{item.note}</span>}
                </span>
                {item.goto && (
                  <a href={item.goto.href}>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<ArrowRightIcon size={13} weight="bold" />}
                    >
                      {item.goto.label}
                    </Button>
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
        </Panel>
      </div>

      <Panel title="Latest submissions" flush>
        {state.loading ? (
          <RowSkeleton rows={5} />
        ) : recent.length === 0 ? (
          <EmptyState
            icon={<TrayIcon size={22} />}
            title="No submissions yet today"
            body="Leads appear here the moment a form is submitted on any of the sites."
          />
        ) : (
          <div className="records records--stagger">
            {recent.map((lead) => {
              const status = qualityTone(lead);
              return (
                <article className="record" key={lead.id}>
                  <div className="record__primary">
                    <p className="record__name">{lead.name || "Unnamed enquiry"}</p>
                    <p className="record__meta">
                      {lead.company || siteName(lead.siteKey)}
                    </p>
                  </div>
                  <div className="record__contact">
                    {lead.email && <a href={`mailto:${lead.email}`}>{lead.email}</a>}
                    {lead.phone && <a href={`tel:${lead.phone}`}>{lead.phone}</a>}
                  </div>
                  <div className="record__aside">
                    <span className="mono">{relativeTime(lead.submittedAt)}</span>
                  </div>
                  <div className="record__actions">
                    <Badge tone={status.tone}>{status.label}</Badge>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </Panel>
    </div>
  );
}
